import React from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { formatMonthForDisplay } from "../../../../utils/dateUtils";
import { escapeCSV } from "../../../../utils/csvUtils";
// Helper function to format month for display (2024-05 -> 2024-May)

const GottonReportHeader = ({
  uniqueMonths,
  selectedMonth,
  handleMonthChange,
  filteredCustomers,
  customerMessages,
  loading,
  messagesLoading,
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  const handleDownloadCSV = () => {
    let csvHeaders, csvRows, fileName;

    if (selectedMonth === "All") {
      csvHeaders = ["Month", "Full Name", "Total Customers", "Total Offers"];
      csvRows = filteredCustomers.map((customer) =>
        [
          escapeCSV(formatMonthForDisplay(customer.MONTH)),
          escapeCSV(`${customer.first_name} ${customer.last_name}`),
          customer.totalCustomers,
          customer.totalMessages,
        ].join(",")
      );
      fileName = `Customers_All.csv`;
    } else {
      csvHeaders = [
        "Vendor Name",
        "Customer Email",
        "Customer Full Name",
        "Created At",
        "Need Title",
        "Need Description",
      ];
      csvRows = customerMessages.map((message) =>
        [
          message.vendor_name,
          escapeCSV(message.customer_email),
          escapeCSV(
            `${message.customer_first_name} ${message.customer_last_name}`
          ),
          escapeCSV(new Date(message.created_at).toLocaleString()),
          escapeCSV(message.need_title),
          escapeCSV(message.need_description),
        ].join(",")
      );
      const firstCustomer = customerMessages[0];
      fileName = firstCustomer
        ? `${firstCustomer.customer_first_name}_${selectedMonth}.csv`
        : `Messages_${selectedMonth}.csv`;
    }

    const csvContent = [csvHeaders.join(","), ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: 2,
        flexDirection: { xs: "column", sm: "row" }, // Stack on mobile, row on tablet+
        gap: { xs: 1, sm: 2 }, // Reduced gap on mobile
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1, sm: 2 },
          width: { xs: "100%", sm: "auto" }, // Full width on mobile
        }}
      >
        <Button
          variant="outlined"
          onClick={handleBack}
          sx={{
            minWidth: { xs: "auto", sm: "auto" },
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 500,
            fontSize: { xs: "0.75rem", sm: "0.875rem" }, // Smaller font on mobile
            padding: { xs: "4px 8px", sm: "6px 12px" }, // Smaller padding on mobile
          }}
        >
          ← Back
        </Button>
        <Typography
          variant="h5"
          sx={{
            fontSize: { xs: "1.25rem", sm: "1.5rem" }, // Smaller on mobile
          }}
        >
          {`Customers Gotten Count ${
            customerMessages.length > 0 ? `(${customerMessages.length})` : ""
          }`}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1, sm: 2 },
          width: { xs: "100%", sm: "auto" }, // Full width on mobile
          flexDirection: { xs: "column", sm: "row" }, // Stack on mobile
        }}
      >
        <Box sx={{ width: { xs: "100%", sm: 180 } }}>
          <FormControl fullWidth>
            <InputLabel id="month-filter-label">Filter by Month</InputLabel>
            <Select
              labelId="month-filter-label"
              value={selectedMonth}
              label="Filter by Month"
              onChange={handleMonthChange}
              sx={{
                borderRadius: "8px",
                height: { xs: "36px", sm: "40px" }, // Smaller height on mobile
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "grey.400",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "primary.main",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "primary.main",
                },
                "& .MuiSelect-select": {
                  padding: { xs: "8px 12px", sm: "10px 14px" },
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                },
              }}
            >
              {uniqueMonths.map((month) => (
                <MenuItem
                  key={month}
                  value={month}
                  sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                >
                  {month}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Button
          variant="contained"
          onClick={handleDownloadCSV}
          disabled={loading || messagesLoading}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 500,
            fontSize: { xs: "0.75rem", sm: "0.875rem" }, // Smaller font on mobile
            padding: { xs: "4px 8px", sm: "6px 12px" }, // Smaller padding on mobile
            width: { xs: "100%", sm: "auto" }, // Full width on mobile
          }}
        >
          Download as CSV
        </Button>
      </Box>
    </Box>
  );
};

export default GottonReportHeader;
